/**
 * Modules JavaScript
 * Specific functionality for Joomla modules
 */

class ModuleEnhancements {
    constructor() {
        this.init();
    }

    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.setupModules();
            });
        } else {
            this.setupModules();
        }
    }

    setupModules() {
        this.initAccordionModules();
        this.initTabModules();
        this.initCarouselModules();
        this.initCounterModules();
        this.initModalTriggers();
        this.initImageGallery();
        this.initSocialShare();
    }

    /**
     * Initialize accordion functionality for modules
     */
    initAccordionModules() {
        const accordions = document.querySelectorAll('.module-accordion');
        
        accordions.forEach(accordion => {
            const triggers = accordion.querySelectorAll('.accordion-trigger');
            
            triggers.forEach(trigger => {
                trigger.addEventListener('click', (e) => {
                    e.preventDefault();
                    
                    const content = trigger.nextElementSibling;
                    const isActive = trigger.classList.contains('active');
                    
                    // Close all other accordions in this module
                    triggers.forEach(t => {
                        t.classList.remove('active');
                        t.nextElementSibling.style.maxHeight = null;
                    });
                    
                    // Toggle current accordion
                    if (!isActive) {
                        trigger.classList.add('active');
                        content.style.maxHeight = content.scrollHeight + 'px';
                    }
                });
            });
        });
    }

    /**
     * Initialize tab functionality for modules
     */
    initTabModules() {
        const tabContainers = document.querySelectorAll('.module-tabs');
        
        tabContainers.forEach(container => {
            const tabs = container.querySelectorAll('.tab-trigger');
            const contents = container.querySelectorAll('.tab-content');
            
            tabs.forEach((tab, index) => {
                tab.addEventListener('click', (e) => {
                    e.preventDefault();
                    
                    // Remove active class from all tabs and contents
                    tabs.forEach(t => t.classList.remove('active'));
                    contents.forEach(c => c.classList.remove('active'));
                    
                    // Add active class to clicked tab and corresponding content
                    tab.classList.add('active');
                    if (contents[index]) {
                        contents[index].classList.add('active');
                    }
                });
            });
        });
    }

    /**
     * Initialize carousel functionality for modules
     */
    initCarouselModules() {
        const carousels = document.querySelectorAll('.module-carousel');
        
        carousels.forEach(carousel => {
            const slides = carousel.querySelectorAll('.carousel-item');
            const prevBtn = carousel.querySelector('.carousel-prev');
            const nextBtn = carousel.querySelector('.carousel-next');
            const indicators = carousel.querySelectorAll('.carousel-indicator');
            
            let currentSlide = 0;
            const totalSlides = slides.length;
            
            if (totalSlides === 0) return;
            
            const showSlide = (index) => {
                slides.forEach((slide, i) => {
                    slide.classList.toggle('active', i === index);
                });
                
                indicators.forEach((indicator, i) => {
                    indicator.classList.toggle('active', i === index);
                });
                
                currentSlide = index;
            };
            
            const nextSlide = () => {
                const next = (currentSlide + 1) % totalSlides;
                showSlide(next);
            };
            
            const prevSlide = () => {
                const prev = (currentSlide - 1 + totalSlides) % totalSlides;
                showSlide(prev);
            };
            
            // Event listeners
            if (nextBtn) nextBtn.addEventListener('click', nextSlide);
            if (prevBtn) prevBtn.addEventListener('click', prevSlide);
            
            indicators.forEach((indicator, index) => {
                indicator.addEventListener('click', () => showSlide(index));
            });
            
            // Auto-play
            const autoPlay = carousel.dataset.autoplay;
            if (autoPlay === 'true') {
                setInterval(nextSlide, 5000);
            }
            
            // Initialize first slide
            showSlide(0);
        });
    }

    /**
     * Initialize counter animation for modules
     */
    initCounterModules() {
        const counters = document.querySelectorAll('.counter-number');
        
        if ('IntersectionObserver' in window) {
            const counterObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const counter = entry.target;
                        const target = parseInt(counter.dataset.target);
                        const duration = parseInt(counter.dataset.duration) || 2000;
                        
                        this.animateCounter(counter, target, duration);
                        counterObserver.unobserve(counter);
                    }
                });
            });
            
            counters.forEach(counter => counterObserver.observe(counter));
        }
    }

    /**
     * Animate counter to target value
     */
    animateCounter(element, target, duration) {
        const start = 0;
        const increment = target / (duration / 16);
        let current = start;
        
        const timer = setInterval(() => {
            current += increment;
            element.textContent = Math.floor(current);
            
            if (current >= target) {
                element.textContent = target;
                clearInterval(timer);
            }
        }, 16);
    }

    /**
     * Initialize modal triggers in modules
     */
    initModalTriggers() {
        const triggers = document.querySelectorAll('[data-module-modal]');
        
        triggers.forEach(trigger => {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                
                const modalId = trigger.dataset.moduleModal;
                const modal = document.getElementById(modalId);
                
                if (modal) {
                    const bsModal = new bootstrap.Modal(modal);
                    bsModal.show();
                }
            });
        });
    }

    /**
     * Initialize image gallery functionality
     */
    initImageGallery() {
        const galleries = document.querySelectorAll('.module-gallery');
        
        galleries.forEach(gallery => {
            const images = gallery.querySelectorAll('.gallery-item img');
            
            images.forEach((img, index) => {
                img.addEventListener('click', () => {
                    this.openLightbox(images, index);
                });
            });
        });
    }

    /**
     * Open lightbox for image gallery
     */
    openLightbox(images, startIndex) {
        // Create lightbox HTML
        const lightbox = document.createElement('div');
        lightbox.className = 'lightbox-overlay';
        lightbox.innerHTML = `
            <div class="lightbox-container">
                <button class="lightbox-close">&times;</button>
                <button class="lightbox-prev">&#10094;</button>
                <img class="lightbox-image" src="" alt="">
                <button class="lightbox-next">&#10095;</button>
                <div class="lightbox-counter">
                    <span class="current">1</span> / <span class="total">${images.length}</span>
                </div>
            </div>
        `;
        
        document.body.appendChild(lightbox);
        
        const lightboxImg = lightbox.querySelector('.lightbox-image');
        const closeBtn = lightbox.querySelector('.lightbox-close');
        const prevBtn = lightbox.querySelector('.lightbox-prev');
        const nextBtn = lightbox.querySelector('.lightbox-next');
        const currentSpan = lightbox.querySelector('.current');
        
        let currentIndex = startIndex;
        
        const showImage = (index) => {
            lightboxImg.src = images[index].src;
            currentSpan.textContent = index + 1;
            currentIndex = index;
        };
        
        const nextImage = () => {
            const next = (currentIndex + 1) % images.length;
            showImage(next);
        };
        
        const prevImage = () => {
            const prev = (currentIndex - 1 + images.length) % images.length;
            showImage(prev);
        };
        
        // Event listeners
        closeBtn.addEventListener('click', () => {
            document.body.removeChild(lightbox);
        });
        
        nextBtn.addEventListener('click', nextImage);
        prevBtn.addEventListener('click', prevImage);
        
        lightbox.addEventListener('click', (e) => {
            if (e.target === lightbox) {
                document.body.removeChild(lightbox);
            }
        });
        
        // Keyboard navigation
        document.addEventListener('keydown', function keyHandler(e) {
            if (e.key === 'Escape') {
                document.body.removeChild(lightbox);
                document.removeEventListener('keydown', keyHandler);
            } else if (e.key === 'ArrowRight') {
                nextImage();
            } else if (e.key === 'ArrowLeft') {
                prevImage();
            }
        });
        
        // Show initial image
        showImage(startIndex);
    }

    /**
     * Initialize social share functionality
     */
    initSocialShare() {
        const shareButtons = document.querySelectorAll('.social-share-btn');
        
        shareButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                
                const platform = btn.dataset.platform;
                const url = encodeURIComponent(window.location.href);
                const title = encodeURIComponent(document.title);
                
                let shareUrl = '';
                
                switch (platform) {
                    case 'facebook':
                        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
                        break;
                    case 'twitter':
                        shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
                        break;
                    case 'linkedin':
                        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
                        break;
                    case 'email':
                        shareUrl = `mailto:?subject=${title}&body=${url}`;
                        break;
                }
                
                if (shareUrl) {
                    if (platform === 'email') {
                        window.location.href = shareUrl;
                    } else {
                        window.open(shareUrl, 'share', 'width=600,height=400');
                    }
                }
            });
        });
    }
}

// Initialize module enhancements
new ModuleEnhancements();
